// Considered: forking a `t-composer`-shaped layout inline in the Sphere Type Composer — rejected
// (spec Reuse Audit) because no existing region × selection-state owner exists in
// component-capabilities.json and the Aspect Editor rail prototype needs the identical shell.
// A generic, type-agnostic layout organism lets both consumers share one selection-state
// vocabulary (`data-sel` / `data-ghost` / `data-bad`) instead of duplicating it. See spec Reuse
// Audit and EAINT-921 Phase 1.
//
// Source: `type-rail/trail-composer.jsx:877` (`.t-composer[data-altitude]`), `:930` (the
// `.t-body` index/document/rail row), `aspect-rail/rail.css:163` (rail width
// `clamp(320px,26vw,392px)`). Block/row selection state classes (`rail.css:7-17,204-207`;
// `type-rail/trail.css:40-44`) are owned by the block-level molecule this shell wraps, not by the
// shell itself.
//
// Deliberately carries no string at all — every piece of content, the landmarks and their labels
// included, is a slot the consumer fills.

import React from 'react';
import './EditorRailShellOrganism.scss';

/** Where the reader is standing relative to the document — the prototype's altitude ladder
 * (`type-rail/trail-app.jsx:14-22`). Purely a DOM attribute the consumer's own banner/CSS reacts
 * to; this organism assigns no meaning to any value beyond passing it through. */
export type EditorRailAltitude = 'parent' | 'type' | 'approach' | 'sphere';

export interface EditorRailShellOrganismProps {
  /** Passed straight through as `data-altitude` — see {@link EditorRailAltitude}. */
  altitude?: EditorRailAltitude;
  /**
   * The document is read-only end to end (platform-owned, sealed, sealed ancestor, no
   * permission). Required rather than defaulted so no consumer can forget to declare it — an
   * editor rendered writable by omission is a worse failure than one forced to state its mode.
   */
  locked: boolean;
  /** Altitude/provenance banner, rendered above the header. Typically a `MessageAtom`. */
  banner?: React.ReactNode;
  /** Identity strip / title row (`trail-composer.jsx:878-916`'s `.t-head`). */
  header?: React.ReactNode;
  /** Section index slot (`.t-index`); the content brings its own `<nav>` landmark. */
  index?: React.ReactNode;
  /** Inspector rail slot (`.t-rail`); the content brings its own `<aside>` landmark. */
  rail?: React.ReactNode;
  /** Publish-gate / footer bar, rendered below the document row. */
  footer?: React.ReactNode;
  /** The document itself — the block list the index and rail act on. */
  children?: React.ReactNode;
  className?: string;
  /**
   * Passed straight through as `data-region` on the document wrapper — the element this shell
   * itself bounds and scrolls (`overflow: auto`, stretched to the body row's height). A consumer's
   * own inner content div is never the right node to mark: it has no height of its own (auto,
   * sized to its full content) and only ever gets clipped by this wrapper, so measuring it reports
   * the unclipped content height instead of the bounded/scrolled frame. Optional and unset by
   * default so a consumer that doesn't participate in region measurement (e.g. the Aspect Editor
   * rail stub) gets no attribute at all.
   */
  documentRegion?: string;
  /**
   * Same as `documentRegion`, for the index wrapper: the element that stretches to the body row
   * and scrolls the section list when it outgrows the viewport. The index `<nav>` inside it is
   * content-sized and never scrolls, so a region attribute on the nav measured an unbounded box
   * and could never evidence `index: scrolls` (capture-app's pinning verdict).
   */
  indexRegion?: string;
  /**
   * Same as `indexRegion`, for the rail wrapper: the element that stretches to the body row and
   * scrolls the rail's content when it outgrows the viewport. The rail's own `<aside>` is
   * content-sized and must not carry its own `overflow: auto` — two nested scrollers left the
   * prototype's single-clip, single-scroll rail (`overflow: hidden` on the outer, one scrolling
   * panel inside) as two independently scrollable boxes (TRT-3).
   */
  railRegion?: string;
}

/**
 * Generic rail-pattern editor shell: banner, header, index nav, document, inspector rail, footer
 * — in that DOM order. Owns only layout and the altitude/locked attributes; every visual and
 * textual detail belongs to the slot content — including the landmarks. The index rail is its own
 * `<nav>` and the inspector rail its own `<aside>`, each labelled under the host's namespace, so
 * the slot wrappers here are plain `<div>`s: a second `<nav>` around a `<nav>` is two identically
 * purposed landmarks per page for a screen-reader user to pick between (ux-craft A3). Extracted
 * from the Sphere Type Editor's rail-pattern prototype so a second consumer (the Aspect Editor rail
 * stub, Phase 4) can reuse the same shell without forking it.
 */
const EditorRailShellOrganism: React.FC<EditorRailShellOrganismProps> = ({
  altitude,
  locked,
  banner,
  header,
  index,
  rail,
  footer,
  children,
  className = '',
  documentRegion,
  indexRegion,
  railRegion,
}) => {

  const classes = ['editor-rail-shell', className].filter(Boolean).join(' ').trim();

  return (
    <div
      className={classes}
      data-altitude={altitude}
      data-locked={locked ? '1' : undefined}
    >
      {banner && (
        <div className="editor-rail-shell__banner" data-testid="editor-rail-shell-banner">
          {banner}
        </div>
      )}
      {header && <div className="editor-rail-shell__header">{header}</div>}
      <div className="editor-rail-shell__body">
        {index && (
          <div className="editor-rail-shell__index" data-region={indexRegion}>
            {index}
          </div>
        )}
        <div className="editor-rail-shell__document" data-region={documentRegion}>{children}</div>
        {rail && (
          <div className="editor-rail-shell__rail" data-region={railRegion}>
            {rail}
          </div>
        )}
      </div>
      {footer && <div className="editor-rail-shell__footer">{footer}</div>}
    </div>
  );
};

EditorRailShellOrganism.displayName = 'EditorRailShellOrganism';

export default EditorRailShellOrganism;
